import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { orderBy, uniq } from "lodash"

import Modal, { IModalProps } from "components/Modal/Modal"
import { Flex, FlexItem } from "components/Flex"
import Button from "components/Button/Button"
import { Guid } from "@/types"
import ReservationArticle from "models/ReservationArticle"
import Select from "components/Select/Select"
import { Purpose, SortDirection } from "config/constants"
import toastMessages from "stores/toastMessages"
import { ShippingOrderType } from "models/ShippingOrder"
import Table, { TableColumn, TableRow } from "components/Table/Table"
import Checkbox from "components/Checkbox/Checkbox"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import Text from "components/Text/Text"
import NoValue from "components/NoValue/NoValue"
import Lmid from "components/Lmid/Lmid"
import ReservationAssetShippingStatuses from "components/ReservationAssetShippingStatuses/ReservationAssetShippingStatuses"
import ShowFeaturesButton from "components/ShowFeaturesButton/ShowFeaturesButton"
import ReservationAsset from "models/ReservationAsset"
import { AssetShippingStatus } from "models/ShippingOrderAsset"
import useAssetDisplayStatuses from "hooks/useAssetDisplayStatuses"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import { Dayjs } from "utils/dayjs"

interface ISelectAssetsForShippingOrderModalProps {
    shippingOrderType: ShippingOrderType
    onClose: IModalProps["onClose"]
    selectedAssetIds: Guid[]
    articles: ReservationArticle[]
    purpose: Purpose
    nodeArticleId: Guid
    reservationStart?: Dayjs | null
    reservationEnd?: Dayjs | null
    onAssetsSelect(assetsToAdd: Guid[], assetsToDelete: Guid[]): Promise<void>
}

export enum SelectAssetsForShippingOrderModalFilter {
    all = "all",
    released = "released",
    notReleased = "notReleased",
}

const ShippingOrderTypeToFilterTypeMap = {
    [ShippingOrderType.delivery]: SelectAssetsForShippingOrderModalFilter.notReleased,
    [ShippingOrderType.return]: SelectAssetsForShippingOrderModalFilter.released,
}

enum AssetsSortBy {
    articleDescription = "articleDescription",
}

function shouldDisplayAsset(asset: ReservationAsset, filter: SelectAssetsForShippingOrderModalFilter) {
    if (!asset.isAvailable) return false
    if (filter === SelectAssetsForShippingOrderModalFilter.released) {
        return asset.currentShippingStatus === AssetShippingStatus.released
    } else if (filter === SelectAssetsForShippingOrderModalFilter.notReleased) {
        return asset.currentShippingStatus !== AssetShippingStatus.released
    }
    return true
}

const SelectAssetsForShippingOrderModal: FC<ISelectAssetsForShippingOrderModalProps> = ({
    shippingOrderType,
    onClose,
    selectedAssetIds: selectedAssetIdsProp,
    articles,
    purpose,
    nodeArticleId,
    reservationStart,
    reservationEnd,
    onAssetsSelect,
}) => {
    const { t } = useTranslation()
    const [isSaving, setIsSaving] = useState(false)
    const [filter, setFilter] = useState(ShippingOrderTypeToFilterTypeMap[shippingOrderType])
    const [selectedAssetIds, setSelectedAssetIds] = useState(selectedAssetIdsProp)
    const selectedAssetsCount = selectedAssetIds.length
    const nodeAssetId = articles.find(article => article.articleId === nodeArticleId)?.assets[0]?.assetId
    const hasEndDate = purpose !== Purpose.sale
    const [sort, setSort] = useState<{ sortBy: AssetsSortBy, sortDirection: SortDirection } | null>(null)

    const allAssets = useMemo(() => {
        return articles.flatMap(article => article.assets).filter(asset => !!asset.assetId && shouldDisplayAsset(asset, SelectAssetsForShippingOrderModalFilter.all))
    }, [articles])

    const allAssetIds = useMemo(() => {
        return allAssets.map(asset => asset.assetId) as Guid[]
    }, [allAssets])

    const { displayStatuses, loading: isLoadingDisplayStatuses } = useAssetDisplayStatuses(allAssetIds)

    const assets = useMemo(() => {
        const filteredAssets = allAssets.filter(asset => shouldDisplayAsset(asset, filter))
        return sort ? orderBy(filteredAssets, a => {
            if (sort.sortBy === AssetsSortBy.articleDescription) {
                return a.altArticleDescription || a.articleDescription
            }
            return ""
        }, sort.sortDirection) : filteredAssets
    }, [allAssets, filter, sort])

    const areSomeVisibleAssetsChecked = assets.length > 0 && assets.some(asset => {
        return !!asset.assetId && selectedAssetIds.includes(asset.assetId)
    })
    const areAllVisibleAssetsSelected = assets.length > 0 && assets.every(asset => {
        return !!asset.assetId && selectedAssetIds.includes(asset.assetId)
    })

    const toggleAllVisibleAssets = useCallback(() => {
        let assetIds: Guid[] = areAllVisibleAssetsSelected ? [...selectedAssetIds] : []

        if (areAllVisibleAssetsSelected) {
            for (const asset of assets) {
                if (!asset.assetId) continue
                assetIds = assetIds.filter(assetId => assetId !== asset.assetId)
            }
        } else {
            for (const asset of assets) {
                if (!asset.assetId) continue
                if (!assetIds.includes(asset.assetId)) {
                    assetIds.push(asset.assetId)
                }
            }
        }

        setSelectedAssetIds(areAllVisibleAssetsSelected
            ? assetIds
            : uniq([
                ...selectedAssetIds,
                ...assetIds,
            ])
        )
    }, [areAllVisibleAssetsSelected, assets, selectedAssetIds])

    const toggleAsset = useCallback((assetId: Guid) => {
        if (selectedAssetIds.includes(assetId)) {
            setSelectedAssetIds(selectedAssetIds.filter(id => id !== assetId))
        } else {
            setSelectedAssetIds(uniq([...selectedAssetIds, assetId]))
        }
    }, [selectedAssetIds])

    function getAssetsToAdd() {
        return orderBy(
            selectedAssetIds.filter(id => !selectedAssetIdsProp.includes(id)),
            assetId => allAssetIds.indexOf(assetId),
            "asc"
        )
    }

    function getAssetstoDelete() {
        return selectedAssetIdsProp.filter(id => !selectedAssetIds.includes(id))
    }

    async function handleSave() {
        setIsSaving(true)

        const assetsToAdd = getAssetsToAdd()
        const assetsToDelete = getAssetstoDelete()

        try {
            await onAssetsSelect(assetsToAdd, assetsToDelete)
            onClose?.()
        } catch (e) {
            toastMessages.showSystemError()
            setIsSaving(false)
        }
    }

    const tableColumns: TableColumn[] = useMemo(() => ([
        {
            title: <Checkbox
                checked={areAllVisibleAssetsSelected}
                partiallyChecked={!areAllVisibleAssetsSelected && areSomeVisibleAssetsChecked}
                onChange={toggleAllVisibleAssets}
            />,
            width: "6rem",
        },
        {
            title: t("common.article"),
            width: "18%",
            sort: sort?.sortBy === AssetsSortBy.articleDescription ? sort.sortDirection : undefined,
            onSortChange: sortDirection => setSort({
                sortBy: AssetsSortBy.articleDescription,
                sortDirection,
            }),
        },
        {
            title: t("common.lmid"),
            width: "16%",
        },
        {
            title: t("location.location"),
            width: "17%",
        },
        {
            title: t("asset.features"),
            width: "12%",
        },
        {
            title: t("asset.shippingState"),
            width: "16%",
        },
        {
            title: t("common.startDate"),
            width: "10.5%",
        },
        ...hasEndDate ? [{
            title: t("common.endDate"),
            width: "10.5%",
        }] : [],
    ]), [areAllVisibleAssetsSelected, areSomeVisibleAssetsChecked, toggleAllVisibleAssets, t, sort, hasEndDate])

    const tableRows: TableRow[] = useMemo(() => {
        return assets.map(asset => {
            const startDate = asset.start || reservationStart
            const endDate = asset.end || reservationEnd
            return [
                <Checkbox
                    checked={!!asset.assetId && selectedAssetIds.includes(asset.assetId)}
                    onChange={() => asset.assetId && toggleAsset(asset.assetId)}
                />,
                <>
                    <ArticleDescription
                        articleDescription={asset.altArticleDescription || asset.articleDescription}
                        articleId={asset.articleId}
                        isNodeArticle={asset.assetId === nodeAssetId}
                    />
                    <Text type="pSmallest" className="color-grey-500">
                        {t("common.articleNo")}: {asset.articleNumber || <NoValue />}
                    </Text>
                </>,
                <Lmid
                    lmid={asset.lmid}
                    assetId={asset.assetId}
                    labeled={asset.isLabeled}
                    inCollection={asset.linkedToAssetCollection}
                />,
                isLoadingDisplayStatuses
                    ? <NoValue>{t("common.loading")}</NoValue>
                    : (displayStatuses?.[asset.assetId!]?.locationName
                        ? <FlexibleContent text={displayStatuses[asset.assetId!].locationName!} />
                        : <NoValue>{t("common.na")}</NoValue>
                    ),
                <div className="d-inline-block relative">
                    {asset.hasFeaturesOrFreightUnits && asset.assetId
                        ? <ShowFeaturesButton assetId={asset.assetId} />
                        : <NoValue />
                    }
                </div>,
                <div className="py-1">
                    <ReservationAssetShippingStatuses
                        asset={asset}
                        purpose={purpose}
                        wrap
                    />
                </div>,
                startDate?.format(t("config.dateLongFormat")) || <NoValue />,
                ...hasEndDate ? [endDate?.format(t("config.dateLongFormat")) || <NoValue />] : [],
            ]
        })
    }, [assets, displayStatuses, hasEndDate, isLoadingDisplayStatuses, nodeAssetId, purpose, reservationEnd,
        reservationStart, selectedAssetIds, t, toggleAsset])

    const highlightRowIndexes = useMemo(() => {
        return selectedAssetIds.map(id => assets.findIndex(asset => asset.assetId === id))
    }, [assets, selectedAssetIds])

    return (
        <Modal
            title={t('selectAssetsForShippingOrder.title')}
            maxWidth="1440px"
            onClose={onClose}
            footer={
                <Flex alignItems="center" justifyContent="space-between">
                    <FlexItem className="color-grey-500">
                        {t('selectAssetsForShippingOrder.xAssetsSelected', { count: selectedAssetsCount })}
                    </FlexItem>
                    <Flex>
                        <Button
                            theme="secondary"
                            label={t('common.cancel')}
                            onClick={onClose}
                            disabled={isSaving} />
                        <Button
                            label={t('selectAssetsForShippingOrder.selectXAssets', { count: selectedAssetsCount })}
                            onClick={handleSave}
                            className="ml-2"
                            disabled={isSaving}
                            loading={isSaving} />
                    </Flex>
                </Flex>
            }>
            <Flex flow="column" className="max-h-100 overflow-hidden">
                <Flex justifyContent="space-between"
                    alignItems="center"
                    className="mb-3">
                    <FlexItem>
                        {sort && assets.length > 0
                            ? <Button
                                theme="link"
                                label={t('shippingOrders.resetToDefaultOrder')}
                                onClick={() => setSort(null)}
                            />
                            : null
                        }
                    </FlexItem>
                    <FlexItem>
                        {/* <FlexItem>
                            <Checkbox
                                label={t('selectAssetsForShippingOrder.selectAllAssets')}
                                checked={areAllVisibleAssetsSelected}
                                onChange={toggleAllVisibleAssets}
                                disabled={!areAssetsFound}
                            />
                        </FlexItem> */}

                        <Select
                            options={[
                                { label: t('selectAssetsForShippingOrder.filter.allAssets'), value: SelectAssetsForShippingOrderModalFilter.all },
                                { label: t('selectAssetsForShippingOrder.filter.releasedAssets'), value: SelectAssetsForShippingOrderModalFilter.released },
                                { label: t('selectAssetsForShippingOrder.filter.notReleasedAssets'), value: SelectAssetsForShippingOrderModalFilter.notReleased },
                            ]}
                            value={filter}
                            onChange={option => setFilter(option!.value)}
                            isInModal
                        />
                    </FlexItem>
                </Flex>
                {assets.length > 0 &&
                    <div className="max-h-100 overflow-auto position-relative">
                        <Table
                            columns={tableColumns}
                            rows={tableRows}
                            tableLayoutFixed
                            stickyHeader
                            highlightRowIndexes={highlightRowIndexes}
                            highlightRowStyle="grey"
                        />
                    </div>
                }
                {/* articles.map(article => (
                    <SelectAssetsForShippingOrderModalArticle
                        key={article.articleId}
                        article={article}
                        isNodeArticle={article.articleId === nodeArticleId}
                        selectedAssetIds={selectedAssetIds}
                        purpose={purpose}
                        filter={filter}
                        isExpanded={!collapsedArticleIds.includes(article.articleId)}
                        reservationStart={reservationStart}
                        reservationEnd={reservationEnd}
                        onToggleArticle={toggleArticle}
                        onToggleAsset={toggleAsset} />
                )) */}
                {!assets.length &&
                    <div className="text-center color-grey-500 py-10">
                        {t('selectAssetsForShippingOrder.noAssets')}
                    </div>
                }
            </Flex>
        </Modal>
    )
}

export default SelectAssetsForShippingOrderModal
